import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { X, Star, MapPin, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

function SharePage() {
  const { ratingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratingData, setRatingData] = useState(null);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('ratings')
          .select(`
            id,
            overall,
            price,
            ambiance,
            crowd,
            service,
            drink_selection,
            is_dive_bar,
            review_text,
            created_at,
            profiles:user_id (username, display_name, avatar_url),
            bars:bar_id (id, name, neighborhood)
          `)
          .eq('id', ratingId)
          .single();

        if (fetchError || !data) {
          setError('Rating not found');
          setLoading(false);
          return;
        }

        setRatingData(data);
      } catch (err) {
        console.error('Error fetching shared rating:', err);
        setError('Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    if (ratingId) {
      fetchRating();
    }
  }, [ratingId]);

  // Navigate to bar detail page
  const goToBar = () => {
    if (ratingData?.bars?.id) {
      navigate(`/bar/${ratingData.bars.id}`);
    } else {
      navigate('/');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-dive-black flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-dive-neon animate-spin" />
          <p className="text-gray-400 text-sm">Loading rating...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !ratingData) {
    return (
      <div className="fixed inset-0 bg-dive-black flex items-center justify-center z-50 p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-dive-gray/30 flex items-center justify-center">
            <Star className="w-8 h-8 text-gray-500" />
          </div>
          <h2 className="text-white text-xl font-display mb-2">Rating Not Found</h2>
          <p className="text-gray-400 text-sm mb-6">
            This rating may have been removed or the link may be invalid.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-dive-neon text-white rounded-lg hover:bg-dive-neon/90 transition-colors"
          >
            Explore Bars
          </Link>
        </div>
      </div>
    );
  }

  const { bars: bar, profiles: reviewer } = ratingData;
  const overall = parseFloat(ratingData.overall);
  const username = reviewer?.username || reviewer?.display_name || 'User';
  const displayName = reviewer?.display_name || reviewer?.username || 'User';

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Sub-rating items
  const subRatings = [
    { label: 'Price', value: ratingData.price, icon: '$', color: 'text-green-400' },
    { label: 'Vibe', value: ratingData.ambiance, icon: '🍸', color: 'text-purple-400' },
    { label: 'Crowd', value: ratingData.crowd, icon: '👥', color: 'text-blue-400' },
    { label: 'Svc', value: ratingData.service, icon: '⚡', color: 'text-yellow-400' },
    { label: 'Drinks', value: ratingData.drink_selection, icon: '🍺', color: 'text-red-400' },
  ].filter(r => r.value != null);

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
      onClick={goToBar}
    >
      {/* Close button */}
      <button
        onClick={goToBar}
        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors z-10"
        style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
      >
        <X className="w-6 h-6" />
      </button>

      {/* Rating Card */}
      <div
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-dive-darker to-dive-dark border border-dive-neon/30 rounded-2xl overflow-hidden shadow-2xl shadow-dive-neon/10">
          {/* Card Content */}
          <div className="p-8 text-center">
            {/* Logo */}
            <img
              src="/logo-full.png"
              alt="Tilted"
              className="h-16 mx-auto mb-6"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />

            {/* Bar Name */}
            <h2 className="text-white text-2xl font-display tracking-wide mb-1">{bar?.name}</h2>
            <div className="flex items-center justify-center gap-1.5 text-gray-400 text-sm mb-6">
              <MapPin className="w-3.5 h-3.5" />
              <span>{bar?.neighborhood}</span>
            </div>

            {/* Stars */}
            <div className="flex justify-center gap-1.5 mb-3">
              {[1, 2, 3, 4, 5].map((star) => {
                const filled = overall >= star;
                const half = overall >= star - 0.5 && overall < star;

                return (
                  <div key={star} className="relative w-10 h-10">
                    <svg
                      className="w-10 h-10 text-gray-600 absolute inset-0"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    {filled && (
                      <svg
                        className="w-10 h-10 text-amber-400 absolute inset-0"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    )}
                    {half && (
                      <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                        <svg
                          className="w-10 h-10 text-amber-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Rating Number */}
            <div className="text-amber-400 text-3xl font-bold mb-4">
              {overall.toFixed(1)}/5
            </div>

            {/* Dive Bar Badge */}
            {ratingData.is_dive_bar && (
              <div className="inline-flex items-center gap-1 px-3 py-1 border border-amber-400 text-amber-400 rounded-full text-xs mb-4">
                Voted Dive Bar
              </div>
            )}

            {/* Sub-ratings */}
            {subRatings.length > 0 && (
              <div className="flex justify-center gap-3 mb-4">
                {subRatings.map((sub) => (
                  <div
                    key={sub.label}
                    className="bg-dive-dark/80 border border-dive-gray/50 rounded-lg px-3 py-2 text-center min-w-[56px]"
                  >
                    <div className="text-xs text-gray-500 mb-0.5">{sub.icon}</div>
                    <div className="text-white text-sm font-bold">{parseFloat(sub.value).toFixed(1)}</div>
                    <div className="text-gray-500 text-[10px]">{sub.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Review Text */}
            {ratingData.review_text && (
              <div className="bg-dive-dark/50 rounded-lg p-3 mb-4 border border-dive-gray/30">
                <p className="text-gray-300 text-sm italic">"{ratingData.review_text}"</p>
              </div>
            )}

            {/* Reviewer Info */}
            <div className="flex items-center justify-center gap-2 mb-2">
              {reviewer?.avatar_url ? (
                <img
                  src={reviewer.avatar_url}
                  alt={displayName}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-dive-neon/20 flex items-center justify-center">
                  <span className="text-dive-neon text-xs font-medium">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-dive-neon text-sm">
                Reviewed by @{username}
              </span>
            </div>
            <p className="text-gray-600 text-xs">{timeAgo(ratingData.created_at)}</p>

            {/* Footer branding */}
            <div className="border-t border-dive-gray/50 pt-3 mt-4">
              <p className="text-amber-400/80 text-xs font-medium">Dive Bar Ratings</p>
              <p className="text-gray-600 text-[10px] mt-0.5">Built for dive bars everywhere</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6 space-y-3">
            {user ? (
              // Signed in: go directly to bar page with auto-open rating modal
              <Link
                to={`/bar/${bar?.id}?rate=true`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-dive-neon text-white rounded-xl font-medium hover:bg-dive-neon/90 transition-colors"
              >
                <Star className="w-5 h-5" />
                Rate This Bar Too
              </Link>
            ) : (
              // Not signed in: go to auth with redirect back to bar page
              <Link
                to={`/auth?redirect=${encodeURIComponent(`/bar/${bar?.id}?rate=true`)}`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-dive-neon text-white rounded-xl font-medium hover:bg-dive-neon/90 transition-colors"
              >
                <Star className="w-5 h-5" />
                Sign In & Rate This Bar
              </Link>
            )}
            <Link
              to={`/bar/${bar?.id}`}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dive-gray text-gray-300 rounded-xl font-medium hover:bg-dive-gray/30 hover:text-white transition-colors"
            >
              View Bar Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharePage;
